export const paths = {
  root: '/',
  welcome: '/welcome',
  auth: '/auth',
  verify: '/auth/verify',
  completeProfile: '/complete-profile',
  legalAcceptance: '/legal/acceptance',
  home: '/home',
  assessment: '/assessment',
  assessmentIntro: '/assessment/intro',
  assessmentAttempt: (id) => `/assessment/${id}`,
  results: '/results',
  result: (id) => `/results/${id}`,
  history: '/history',
  consultations: '/consultations',
  consultationNew: '/consultations/new',
  consultation: (id) => `/consultations/${id}`,
  notifications: '/notifications',
  support: '/support',
  supportNew: '/support/new',
  supportTicket: (id) => `/support/${id}`,
  profile: '/profile',
  terms: '/legal/terms',
  privacy: '/legal/privacy',
  accountDelete: '/account/delete',
  forbidden: '/forbidden',
  conflict: '/conflict',
  throttled: '/rate-limited',
  companyPlans: '/company-plans',
  companyPlan: (id) => `/company-plans/${id}`,
  companyOnboarding: '/company/onboarding',
  companyPortal: '/company',
  companyPortalPlans: '/company/plans',
  companyPurchases: '/company/purchases',
  companyPurchase: (id) => `/company/purchases/${id}`,
  companyPurchaseStatus: (id) => `/company/purchases/${id}/status`,
  companyCredits: '/company/credits',
  companyCodes: '/company/codes',
  companyParticipants: '/company/participants',
  companyParticipantResult: (attemptId) => `/company/participants/${attemptId}/result`,
  companySettings: '/company/settings',
  companyCodeEntry: '/assessment/company-code',
};

const INTERNAL_PREFIXES = [
  paths.home,
  paths.assessment,
  paths.results,
  paths.history,
  paths.consultations,
  paths.notifications,
  paths.support,
  paths.profile,
  paths.terms,
  paths.privacy,
  paths.accountDelete,
  paths.completeProfile,
  paths.legalAcceptance,
  paths.companyPlans,
  paths.companyPortal,
  paths.companyCodeEntry,
];

export function isInternalPath(value) {
  if (!value || typeof value !== 'string' || !value.startsWith('/')) {
    return false;
  }
  if (value.startsWith('//')) {
    return false;
  }
  return INTERNAL_PREFIXES.some((prefix) => value === prefix || value.startsWith(`${prefix}/`))
    || value === paths.root
    || value === paths.welcome;
}

export function safeReturnTo(value, fallback = paths.home) {
  if (!value || typeof value !== 'string') {
    return fallback;
  }
  try {
    const decoded = decodeURIComponent(value);
    return isInternalPath(decoded) ? decoded : fallback;
  } catch {
    return fallback;
  }
}

export function postAuthPath(user, extras = {}) {
  const step = extras.next_step || user?.next_step;
  if (step === 'complete_profile') {
    return paths.completeProfile;
  }
  if (step === 'legal_acceptance' || step === 'accept_legal') {
    return paths.legalAcceptance;
  }
  if (step === 'home' || step === 'app') {
    return paths.home;
  }
  if (!isProfileComplete(user)) {
    return paths.completeProfile;
  }
  if (needsLegalAcceptance(user)) {
    return paths.legalAcceptance;
  }
  return paths.home;
}

export function destinationAfterLogin(user, extras = {}) {
  const next = postAuthPath(user, extras);
  if (next !== paths.home) {
    return next;
  }
  return safeReturnTo(extras.returnTo, next);
}

export function isProfileComplete(user) {
  if (!user) {
    return false;
  }
  if (typeof user.profile_complete === 'boolean') {
    return user.profile_complete;
  }
  return Boolean(user.name && String(user.name).trim().length >= 2);
}

export function needsLegalAcceptance(user) {
  return Boolean(user?.legal?.requires_acceptance);
}
